import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { NavLink } from "react-router-dom";
import { SolutionConfig } from "../../data/solutions";
import { BotVisual, SolutionVisual } from "./SolutionVisual";

interface ProductPageLayoutProps {
  solution: SolutionConfig;
}

export const ProductPageLayout: React.FC<ProductPageLayoutProps> = ({
  solution,
}) => {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.35], [0, 90]);
  const Icon = solution.icon;

  useEffect(() => {
    const title = `${solution.name} — ${solution.category} | Hiperbite`;
    const descriptionContent = `${solution.description} Conheça ${solution.name}, uma solução da Hiperbite.`;
    document.title = title;
    const meta = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        const key = selector.match(new RegExp(`${attribute}="([^"]+)"`))?.[1];
        if (key) element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };
    meta('meta[name="description"]', "name", descriptionContent);
    meta('meta[property="og:title"]', "property", title);
    meta('meta[property="og:description"]', "property", descriptionContent);
    meta('meta[property="og:url"]', "property", window.location.href);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.href);
    return () => {
      document.title =
        "Hiperbite — Engenharia de Software & Soluções Tecnológicas em Angola";
    };
  }, [solution]);

  return (
    <article
      className="overflow-hidden bg-[#0b0b0c]"
      style={
        { "--product-accent": solution.theme.accent } as React.CSSProperties
      }
    >
      <section className="relative border-b border-white/[0.1] px-6 pb-20 pt-16 md:px-10 md:pb-28 md:pt-24">
        <div
          className="editorial-grid pointer-events-none absolute inset-0 opacity-40"
          aria-hidden="true"
        />
        <motion.div
          style={{ y: heroY }}
          className="relative mx-auto grid max-w-[1440px] items-center gap-14 lg:grid-cols-12"
        >
          <div className="lg:col-span-6">
            <NavLink
              to="/solucoes"
              className="eyebrow mb-10 inline-flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              <ArrowDownRight className="h-3 w-3" /> voltar ao ecossistema
            </NavLink>
            <div className="mb-7 flex items-center gap-3">
              <div
                className="flex h-24 w-24 items-center justify-center overflow-hidden  bg-white/[0.04] p-2"
                style={{ boxShadow: `0 0 24px ${solution.theme.accentSoft}` }}
              >
                {solution.iconUrl ? (
                  <img
                    src={solution.iconUrl}
                    alt={`${solution.name} logo`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Icon
                    className="h-5 w-5"
                    style={{ color: solution.theme.accent }}
                  />
                )}
              </div>
              <span
                className="eyebrow"
                style={{ color: solution.theme.accent }}
              >
                {solution.category}
              </span>
            </div>
            <h1 className="max-w-3xl text-[clamp(3.6rem,7vw,7.6rem)] font-semibold leading-[0.88] text-[#f4f0e9]">
              {solution.name}
            </h1>
            <p className="mt-8 max-w-xl text-xl leading-8 text-[#aaa7a2]">
              {solution.tagline}
            </p>
            <p className="mt-5 max-w-xl text-sm leading-6 text-white/55">
              {solution.description.slice(0, 200)} ...
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <ExternalLinkButton solution={solution} />
              <SolutionLink solution={solution} />
            </div>
          </div>
          <div className="relative lg:col-span-6">
            <div
              className="absolute -inset-10 opacity-30 blur-3xl"
              style={{ background: solution.theme.accentSoft }}
            />
            <div className="group relative">
              {solution.slug === "hiperbot-ia" ? (
                <BotVisual />
              ) : (
                <SolutionVisual solution={solution} />
              )}
            </div>
            <div className="absolute -bottom-5 -left-5 border border-white/15 bg-[#0b0b0c] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.13em] text-white/45">
              <Icon
                className="mr-2 inline h-3 w-3"
                style={{ color: solution.theme.accent }}
              />{" "}
              produto Hiperbite
            </div>
          </div>
        </motion.div>
      </section>

      <section id="overview" data-scroll-reveal className="px-6 py-24 md:px-10 md:py-36">
        <div className="mx-auto grid max-w-[1440px] gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="eyebrow">01 / contexto</span>
            <h2 className="mt-5 text-4xl font-medium leading-tight text-[#f4f0e9] md:text-5xl">
              Tecnologia que começa no problema certo.
            </h2>
          </div>
          <div className="grid gap-10 lg:col-span-7 lg:col-start-6">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.13em] text-white/40">
                Para quem é
              </span>
              <p className="mt-3 max-w-2xl text-lg leading-8 text-[#aaa7a2]">
                {solution.audience}
              </p>
            </div>
            <div className="border-t border-white/[0.12] pt-7">
              <span className="font-mono text-xs uppercase tracking-[0.13em] text-white/40">
                O problema que resolve
              </span>
              <p className="mt-3 max-w-2xl text-lg leading-8 text-[#aaa7a2]">
                {solution.problem}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section data-scroll-reveal className="border-y border-white/[0.1] bg-[#101011] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1440px]">
          <div className="mb-14 flex items-end justify-between gap-6">
            <div>
              <span
                className="eyebrow"
                style={{ color: solution.theme.accent }}
              >
                02 / capacidades
              </span>
              <h2 className="mt-4 text-4xl font-medium text-[#f4f0e9] md:text-6xl">
                Feito para a realidade.
              </h2>
            </div>
            <span className="hidden font-mono text-xs text-white/35 md:block">
              {solution.features.length} módulos de experiência
            </span>
          </div>
          <div className="grid gap-4 md:grid-cols-12">
            {solution.features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className={`border border-white/[0.12] bg-[#181819] p-7 transition duration-500 hover:-translate-y-1 hover:border-white/25 ${index === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}`}
                >
                  <div className="flex h-full flex-col justify-between">
                    <div>
                      <div className="mb-12 flex items-center justify-between">
                        <span className="font-mono text-xs text-white/35">
                          0{index + 1}
                        </span>
                        <FeatureIcon
                          className="h-5 w-5"
                          style={{ color: solution.theme.accent }}
                        />
                      </div>
                      <h3 className="text-2xl font-medium text-[#f4f0e9]">
                        {feature.title}
                      </h3>
                      <p className="mt-3 max-w-md text-sm leading-6 text-[#aaa7a2]">
                        {feature.description}
                      </p>
                    </div>
                    <div
                      className="mt-10 h-px w-12"
                      style={{ background: solution.theme.accent }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-scroll-reveal="right" className="px-6 py-24 md:px-10 md:py-36">
        <div className="mx-auto grid max-w-[1440px] gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">03 / experiência</span>
            <h2 className="mt-5 text-4xl font-medium leading-tight text-[#f4f0e9] md:text-6xl">
              Uma camada de clareza para o trabalho diário.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SolutionVisual solution={solution} />
          </div>
        </div>
      </section>

      <section data-scroll-reveal className="border-y border-white/[0.1] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1440px]">
          <span className="eyebrow">04 / o que muda</span>
          <div className="mt-12 grid gap-0 md:grid-cols-3">
            {solution.highlights.map((highlight, index) => (
              <motion.div
                key={highlight}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.1 }}
                className="border-t border-white/[0.14] py-7 md:border-r md:px-7 md:first:pl-0 md:last:border-r-0"
              >
                <span
                  className="font-mono text-xs"
                  style={{ color: solution.theme.accent }}
                >
                  0{index + 1}
                </span>
                <h3 className="mt-8 max-w-xs text-2xl font-medium text-[#f4f0e9] md:text-3xl">
                  {highlight}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-scroll-reveal="left" className="px-6 py-28 md:px-10 md:py-40">
        <div className="mx-auto grid max-w-[1440px] items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <span className="eyebrow" style={{ color: solution.theme.accent }}>
              05 / próximo passo
            </span>
            <h2 className="mt-6 max-w-4xl text-5xl font-medium leading-[0.95] text-[#f4f0e9] md:text-8xl">
              Pronto para conhecer esta solução?
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pb-2">
            <ExternalLinkButton solution={solution} large />
            <p className="mt-5 text-xs leading-5 text-white/40">
              O website oficial só é exibido quando existe um URL confirmado
              pela Hiperbite.
            </p>
          </div>
        </div>
      </section>
    </article>
  );
};

const ExternalLinkButton: React.FC<{
  solution: SolutionConfig;
  large?: boolean;
}> = ({ solution, large = false }) =>
  solution.externalUrl ? (
    <a
      href={solution.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center gap-8 bg-[#f15c12] px-6 py-4 text-sm font-semibold text-[#0b0b0c] transition hover:bg-[#ff7531] ${large ? "px-7 py-5" : ""}`}
    >
      Saiba mais{" "}
      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
    </a>
  ) : (
    <NavLink
      to="/contactos"
      className={`group inline-flex items-center gap-8 border border-white/20 bg-white/[0.06] px-6 py-4 text-sm font-semibold text-[#f4f0e9] transition hover:border-[#f15c12] hover:text-[#f15c12] ${large ? "px-7 py-5" : ""}`}
    >
      Falar com a Hiperbite{" "}
      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
    </NavLink>
  );

const SolutionLink: React.FC<{ solution: SolutionConfig }> = ({ solution }) =>
  solution.externalUrl ? (
    <a
      href={solution.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group inline-flex items-center gap-2 border-b border-white/30 pb-2 text-sm text-[#f4f0e9] transition-colors hover:border-white hover:text-white"
    >
      Conhecer a solução{" "}
      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
  ) : (
    <a
      href="#overview"
      className="group inline-flex items-center gap-2 border-b border-white/30 pb-2 text-sm text-[#f4f0e9] transition-colors hover:border-white hover:text-white"
    >
      Conhecer a solução{" "}
      <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-y-1" />
    </a>
  );
